import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { DV_360_PLATFORM } from '../../../../constants/selectLists/platformList'
import { lineItemSelector, lineItemWasLoadedSelector } from '../../../../modules/selectors/lineItems'
import { audiencesErrorSelector, audiencesWasLoadedSelector } from '../../../../modules/selectors/audiences'
import {
  dv360ChannelsWasLoadedSelector,
  dv360NegativeKeywordListsWasLoadedSelector,
  dv360TargetingSelector,
  dv360ViewabilityTargetingOptionsWasLoadedSelector,
  targetingErrorSelector,
  targetingWasLoadedSelector
} from '../../../../modules/selectors/dv360Constants'
import { selectedDV360AdAccountSelector } from '../../../../modules/selectors/adAccounts'
import { clearAudiences, getAudiences } from '../../../../modules/actions/audiences'
import {
  clearDv360Targeting,
  clearGetDv360Channels,
  clearGetDv360NegativeKeywordLists,
  clearGetDv360ViewabilityTargetingOptions,
  getDv360Channels,
  getDv360NegativeKeywordLists,
  getDv360Targeting,
  getDv360ViewabilityTargetingOptions
} from '../../../../modules/actions/dv360Constants'
import { targetingTypes } from '../LineItemDv360Create/fields'

export const useLoadAdditionalData = ({ isFormOpen }) => {
  const dispatch = useDispatch()

  const item = useSelector(lineItemSelector)
  const lineItemWasLoaded = useSelector(lineItemWasLoadedSelector)

  const audiencesWasLoaded = useSelector(audiencesWasLoadedSelector)
  const audiencesError = useSelector(audiencesErrorSelector)

  const targeting = useSelector(dv360TargetingSelector)
  const targetingWasLoaded = useSelector(targetingWasLoadedSelector)
  const targetingError = useSelector(targetingErrorSelector)
  const selectedChannelsTargetingOptions = targeting.filter(
    option => option.type === targetingTypes.TARGETING_TYPE_CHANNEL
  )
  const dv360ChannelsWasLoaded = useSelector(dv360ChannelsWasLoadedSelector)
  const selectedNegativeKeywordsListOptions = targeting.filter(
    option => option.type === targetingTypes.TARGETING_TYPE_NEGATIVE_KEYWORD_LIST
  )
  const selectedViewabilityTargetingOptions = targeting.filter(
    option => option.type === targetingTypes.TARGETING_TYPE_VIEWABILITY
  )

  const dv360NegativeKeywordListsWasLoaded = useSelector(dv360NegativeKeywordListsWasLoadedSelector)
  const dv360ViewabilityTargetingOptionsWasLoaded = useSelector(dv360ViewabilityTargetingOptionsWasLoadedSelector)

  const { id: selectedAdAccountId } = useSelector(selectedDV360AdAccountSelector)

  const audiencesDidLoading = audiencesWasLoaded || audiencesError
  const targetingDidLoading = targetingWasLoaded || targetingError

  const isAdditionalDataLoading = !audiencesDidLoading || !targetingDidLoading

  useEffect(() => {
    if (isFormOpen && !audiencesDidLoading) {
      dispatch(
        getAudiences(
          {
            account: selectedAdAccountId,
            filter_by_account_id: true,
            limit: 9999
          },
          DV_360_PLATFORM
        )
      )
    }
  }, [dispatch, selectedAdAccountId, isFormOpen, audiencesDidLoading])

  useEffect(() => {
    if (isFormOpen && lineItemWasLoaded && !targetingDidLoading) {
      dispatch(
        getDv360Targeting({
          account: selectedAdAccountId,
          line_item_id: item.id
        })
      )
    }
  }, [dispatch, item, selectedAdAccountId, isFormOpen, lineItemWasLoaded, targetingDidLoading])

  useEffect(() => {
    // If lineItem has selected channels - fetch channels to be able to represent their names on preview and form
    if (!dv360ChannelsWasLoaded && selectedChannelsTargetingOptions.length > 0) {
      dispatch(
        getDv360Channels({
          account: selectedAdAccountId
        })
      )
    }
  }, [dispatch, selectedAdAccountId, dv360ChannelsWasLoaded, selectedChannelsTargetingOptions])

  useEffect(() => {
    return () => {
      if (dv360ChannelsWasLoaded) {
        dispatch(clearGetDv360Channels())
      }
    }
  }, [dispatch, dv360ChannelsWasLoaded])

  useEffect(() => {
    if (!dv360NegativeKeywordListsWasLoaded && selectedNegativeKeywordsListOptions) {
      dispatch(
        getDv360NegativeKeywordLists({
          account: selectedAdAccountId
        })
      )
    }
  }, [dispatch, selectedAdAccountId, dv360NegativeKeywordListsWasLoaded, selectedNegativeKeywordsListOptions])

  useEffect(() => {
    if (selectedViewabilityTargetingOptions.length) {
      dispatch(
        getDv360ViewabilityTargetingOptions({
          account: selectedAdAccountId
        })
      )
    }
  }, [dispatch, selectedAdAccountId, selectedViewabilityTargetingOptions])

  useEffect(() => {
    return () => {
      if (dv360ViewabilityTargetingOptionsWasLoaded) {
        dispatch(clearGetDv360ViewabilityTargetingOptions())
      }
    }
  }, [dispatch, dv360ViewabilityTargetingOptionsWasLoaded])

  useEffect(() => {
    return () => {
      if (dv360NegativeKeywordListsWasLoaded) {
        dispatch(clearGetDv360NegativeKeywordLists())
      }
    }
  }, [dispatch, dv360NegativeKeywordListsWasLoaded])

  useEffect(
    () => () => {
      dispatch(clearDv360Targeting())
      dispatch(clearAudiences())
    },
    [dispatch]
  )

  return isAdditionalDataLoading
}
